import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRecoilState } from 'recoil';

import { deleteMember, inviteMember, syncTrainingDelegations, updateMember } from 'api/Member';

import { formatDate } from 'lib/helpers/dateUtils';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import { validateAvailableTimes } from 'components/practice/Settings/components/PracticeDetails/lib/workingHoursUtils';
import practiceState from 'components/practice/practiceState';
import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Button/Button';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Modal from 'components/shared/Modal/Modal';

import useNewStaffMemberFormik from '../lib/useNewStaffMemberFormik';

import StaffForm from './StaffForm';

const InviteMemberModal = ({ item: member, isOpen, handleClose }) => {
  const navigate = useNavigate();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [currentPractice] = useRecoilState(practiceState.currentPractice);
  const queryClient = useQueryClient();
  const outletCtx = useOutletContext();

  const currentPracticeId = outletCtx?.practice?.id || currentPractice?.id;

  const initialValues = {
    f_name: member?.f_name || '',
    m_name: member?.m_name || '',
    l_name: member?.l_name || '',
    role: member?.role_id || null,
    profession_group_id: member?.profession_group_id || null,
    gender: member?.gender || null,
    email: member?.email || '',
    dob: member?.dob || '',
    color: member?.color || '',
    phone: member?.phone || '',
    npi: member?.claimMdInfo?.npi || '',
    tax_id: member?.claimMdInfo?.tax_id || '',
    tax_id_type: member?.claimMdInfo?.tax_id_type || '',
    taxonomy: member?.claimMdInfo?.taxonomy || '',
    address: member?.address?.address_ln_1 || '',
    address_2: member?.address?.address_ln_2 || '',
    city: member?.address?.city || '',
    state: member?.address?.state || '',
    zip: member?.address?.zip || '',
    payment_rate: member?.payment_rate ? (member.payment_rate / 100).toFixed(2) : '0',
    is_active: member?.status === 'active' ? true : false,
    local_state_ids: Array.isArray(member?.local_state_ids) ? member.local_state_ids : [],
    virtual_state_ids: Array.isArray(member?.virtual_state_ids) ? member.virtual_state_ids : [],
    availableTimes: (() => {
      let parsed = member?.available_times;
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch (e) {
          parsed = null;
        }
      }
      return Array.isArray(parsed) ? parsed : null;
    })()
  };

  const formik = useNewStaffMemberFormik(initialValues, async (values, formikHelpers) => {
    const { role, dob, availableTimes, trainings, ...user } = values;
    if (member && Array.isArray(availableTimes) && availableTimes.length > 0) {
      const { hasError, errors } = validateAvailableTimes(availableTimes);
      if (hasError) {
        let errorMessage = 'Please fix the errors in your availability times.';
        if (errors && typeof errors === 'object') {
          const errorList = [];
          Object.entries(errors).forEach(([day, errObj]) => {
            if (errObj) {
              if (errObj.breaks) {
                Object.values(errObj.breaks).forEach((msg) => {
                  errorList.push(`${day}: ${msg}`);
                });
              }
              if (errObj.virtual) errorList.push(`${day}: ${errObj.virtual}`);
              if (errObj.location) errorList.push(`${day}: ${errObj.location}`);
            }
          });
          if (errorList.length > 0) errorMessage = errorList.join(', ');
        }
        showAlert({ color: 'danger', message: errorMessage });
        return;
      }
    }

    if (!member) {
      const result = await createAsync({
        user: {
          ...user,
          dob: formatDate(dob),
          payment_rate: Math.round(parseFloat(values.payment_rate) * 100)
        },
        role_id: role,
        practice_id: currentPracticeId,
        ...(availableTimes && { available_times: JSON.stringify(availableTimes) })
      });
      if (result?.new_user_id) {
        await syncTrainingDelegations(navigate, {
          userId: result.new_user_id,
          practiceId: currentPracticeId,
          definitionIds: trainings ?? []
        });
        queryClient.invalidateQueries({
          queryKey: ['wizardInstanceDelegation'],
          refetchType: 'all'
        });
      }
    } else {
      await updateAsync({
        user: {
          id: member.user_id,
          ...user,
          dob: formatDate(dob),
          payment_rate: Math.round(parseFloat(values.payment_rate) * 100)
        },
        role_id: role,
        practice_id: currentPracticeId,
        ...(availableTimes && { available_times: JSON.stringify(availableTimes) })
      });
      await syncTrainingDelegations(navigate, {
        userId: member.user_id,
        practiceId: currentPracticeId,
        definitionIds: trainings ?? []
      });
      queryClient.invalidateQueries({ queryKey: ['wizardInstanceDelegation'], refetchType: 'all' });
    }
    formikHelpers.resetForm();
    handleClose();
  });

  const { mutateAsync: createAsync, isPending } = useCustomMutation({
    mutationFn: (params) =>
      inviteMember(navigate, {
        ...params,
        practice_id: currentPracticeId
      }),
    successMessage: 'Staff member invited successfully!',
    invalidateQueryKey: ['member', 'practice']
  });

  const { mutateAsync: updateAsync, isPending: isUpdatePending } = useCustomMutation({
    mutationFn: (params) =>
      updateMember(navigate, {
        ...params,
        practice_id: currentPracticeId
      }),
    successMessage: 'Staff member updated successfully!',
    invalidateQueryKey: ['member', 'practice']
  });

  const mutateDelete = useMutation({
    mutationFn: () =>
      deleteMember(navigate, {
        memberId: member.user_id,
        practiceId: currentPracticeId
      }),
    onSuccess: (res) => {
      if (res?.code === 0) {
        queryClient.invalidateQueries(['member']);
        showAlert({ title: 'Member deleted successfully', color: 'success' });
      } else {
        showAlert({ title: 'Failed to delete member.', color: 'danger' });
      }
      setShowConfirmDelete(false);
      handleClose();
    }
  });

  const closeModal = () => {
    formik.resetForm();
    handleClose();
  };

  return (
    <>
      <Modal
        handleOpen={isOpen}
        handleClose={closeModal}
        footerActions={{
          other: (
            <Button
              variant="link"
              color="neutral"
              icon="trash-mono"
              text="Delete staff member"
              size="small"
              onClick={() => setShowConfirmDelete(true)}
            />
          ),
          primary: {
            text: member ? 'Update' : 'Invite',
            onClick: formik.handleSubmit,
            loading: isPending || isUpdatePending
          }
        }}
        title={`${member ? 'Edit' : 'Invite'} staff member`}>
        <StaffForm formik={formik} practiceId={currentPracticeId} staffMemberId={member?.user_id} />
      </Modal>
      {showConfirmDelete && (
        <Confirm
          variant="danger"
          title="Delete member"
          icon="trash-mono"
          message="Are you sure you want to delete this member?"
          handleOpen={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          footerActions={{
            primary: {
              text: 'Delete',
              onClick: () => mutateDelete.mutate(),
              color: 'danger'
            }
          }}
        />
      )}
    </>
  );
};

export default InviteMemberModal;
